import { IApi } from '../../api'
import { createRequestTypes } from '../../helpers/helpers'
import { createAction } from '../types'

/**
 * The Loan Committee Report's exhibit slices — Existing Debt, Inventory
 * Appraisal and Board Members — under one prefix rather than a slice each.
 * They serve one page and one api module (`api.lcr`); a slice per exhibit left
 * three near-identical folders and three store keys for the same screen.
 *
 * The analysis record itself lives in `generalAnalysis`; this is only the
 * per-exhibit data hanging off it.
 */
export const prefix = 'lcr'

// ── Existing Debt ───────────────────────────────────────────────────────────

const LIST_EXISTING_DEBT = createRequestTypes(prefix, 'LIST_EXISTING_DEBT')
export const [LIST_EXISTING_DEBT_REQUEST, LIST_EXISTING_DEBT_SUCCESS, LIST_EXISTING_DEBT_FAILURE] =
  LIST_EXISTING_DEBT

export const listExistingDebt = createAction((id: string) => ({
  type: LIST_EXISTING_DEBT,
  promise: (api: IApi) => api.lcr.listExistingDebt(id),
}))

const CREATE_EXISTING_DEBT = createRequestTypes(prefix, 'CREATE_EXISTING_DEBT')
export const [
  CREATE_EXISTING_DEBT_REQUEST,
  CREATE_EXISTING_DEBT_SUCCESS,
  CREATE_EXISTING_DEBT_FAILURE,
] = CREATE_EXISTING_DEBT

export const createExistingDebt = createAction((id: string, data: object) => ({
  type: CREATE_EXISTING_DEBT,
  promise: (api: IApi) => api.lcr.createExistingDebt(id, data),
}))

const UPDATE_EXISTING_DEBT = createRequestTypes(prefix, 'UPDATE_EXISTING_DEBT')
export const [
  UPDATE_EXISTING_DEBT_REQUEST,
  UPDATE_EXISTING_DEBT_SUCCESS,
  UPDATE_EXISTING_DEBT_FAILURE,
] = UPDATE_EXISTING_DEBT

export const updateExistingDebt = createAction((id: string, debtId: string, data: object) => ({
  type: UPDATE_EXISTING_DEBT,
  promise: (api: IApi) => api.lcr.updateExistingDebt(id, debtId, data),
}))

const DELETE_EXISTING_DEBT = createRequestTypes(prefix, 'DELETE_EXISTING_DEBT')
export const [
  DELETE_EXISTING_DEBT_REQUEST,
  DELETE_EXISTING_DEBT_SUCCESS,
  DELETE_EXISTING_DEBT_FAILURE,
] = DELETE_EXISTING_DEBT

export const deleteExistingDebt = createAction((id: string, debtId: string) => ({
  type: DELETE_EXISTING_DEBT,
  promise: (api: IApi) => api.lcr.deleteExistingDebt(id, debtId),
}))

// ── Inventory Appraisal ─────────────────────────────────────────────────────

const GET_INVENTORY_APPRAISAL = createRequestTypes(prefix, 'GET_INVENTORY_APPRAISAL')
export const [
  GET_INVENTORY_APPRAISAL_REQUEST,
  GET_INVENTORY_APPRAISAL_SUCCESS,
  GET_INVENTORY_APPRAISAL_FAILURE,
] = GET_INVENTORY_APPRAISAL

export const getInventoryAppraisal = createAction((id: string) => ({
  type: GET_INVENTORY_APPRAISAL,
  promise: (api: IApi) => api.lcr.getInventoryAppraisal(id),
}))

const UPLOAD_INVENTORY_APPRAISAL = createRequestTypes(prefix, 'UPLOAD_INVENTORY_APPRAISAL')
export const [
  UPLOAD_INVENTORY_APPRAISAL_REQUEST,
  UPLOAD_INVENTORY_APPRAISAL_SUCCESS,
  UPLOAD_INVENTORY_APPRAISAL_FAILURE,
] = UPLOAD_INVENTORY_APPRAISAL

// Upload returns the freshly-extracted appraisal (same DTO as Get), so it is
// wired as a loading trigger too: its SUCCESS replaces `data` and the loader
// shows while the document is being extracted — no second round-trip.
export const uploadInventoryAppraisal = createAction((id: string, file: File) => ({
  type: UPLOAD_INVENTORY_APPRAISAL,
  promise: (api: IApi) => api.lcr.uploadInventoryAppraisal(id, file),
}))

// ── Board Members ───────────────────────────────────────────────────────────

const LIST_LCR_BOARD_MEMBERS = createRequestTypes(prefix, 'LIST_LCR_BOARD_MEMBERS')
export const [
  LIST_LCR_BOARD_MEMBERS_REQUEST,
  LIST_LCR_BOARD_MEMBERS_SUCCESS,
  LIST_LCR_BOARD_MEMBERS_FAILURE,
] = LIST_LCR_BOARD_MEMBERS

export const listLcrBoardMembers = createAction((id: string) => ({
  type: LIST_LCR_BOARD_MEMBERS,
  promise: (api: IApi) => api.lcr.listBoardMembers(id),
}))

const ADD_LCR_BOARD_MEMBER = createRequestTypes(prefix, 'ADD_LCR_BOARD_MEMBER')
export const [
  ADD_LCR_BOARD_MEMBER_REQUEST,
  ADD_LCR_BOARD_MEMBER_SUCCESS,
  ADD_LCR_BOARD_MEMBER_FAILURE,
] = ADD_LCR_BOARD_MEMBER

export const addLcrBoardMember = createAction((id: string, data: object) => ({
  type: ADD_LCR_BOARD_MEMBER,
  promise: (api: IApi) => api.lcr.addBoardMember(id, data),
}))

const UPDATE_LCR_BOARD_MEMBER = createRequestTypes(prefix, 'UPDATE_LCR_BOARD_MEMBER')
export const [
  UPDATE_LCR_BOARD_MEMBER_REQUEST,
  UPDATE_LCR_BOARD_MEMBER_SUCCESS,
  UPDATE_LCR_BOARD_MEMBER_FAILURE,
] = UPDATE_LCR_BOARD_MEMBER

export const updateLcrBoardMember = createAction((id: string, memberId: string, data: object) => ({
  type: UPDATE_LCR_BOARD_MEMBER,
  promise: (api: IApi) => api.lcr.updateBoardMember(id, memberId, data),
}))

const DELETE_LCR_BOARD_MEMBER = createRequestTypes(prefix, 'DELETE_LCR_BOARD_MEMBER')
export const [
  DELETE_LCR_BOARD_MEMBER_REQUEST,
  DELETE_LCR_BOARD_MEMBER_SUCCESS,
  DELETE_LCR_BOARD_MEMBER_FAILURE,
] = DELETE_LCR_BOARD_MEMBER

export const deleteLcrBoardMember = createAction((id: string, memberId: string) => ({
  type: DELETE_LCR_BOARD_MEMBER,
  promise: (api: IApi) => api.lcr.deleteBoardMember(id, memberId),
}))
